import UserDataSource from "data/UserDataSource";

declare var $:any;

import { observable, action, computed } from 'mobx';
import { GET_QUERY, POST_QUERY } from 'utils';
import * as moment from 'moment';
import { UIState } from 'data/UIState';
import { reorder } from 'components/widgets/SortableList';

const TRACKER_DATE_FORMAT = 'YYYY-MM-DD';

export const TRACKER_SRC = {
  FITBIT:'fitbit',
  MANUAL:'strive'
};

interface IDailyValue {
  day: Date;
  value: number;
  src: string;
}

interface ITracker {
  _id: string;
  id: string;
  iconName: string;
  name: string;       // Steps
  unit: string;       // step
  dailyGoal: number;  
  dailyValues:{[id: string]:IDailyValue};
  dailyValuesFitbit:{[id: string]:IDailyValue};
  src: string;        // fitbit, strive
  createdDate: Date;
  activeTracking: boolean;
  sortOrder: number;
  isADefaultTracker: boolean; 
}

interface ITrackerOption {
  id:string;
  iconName:string;
  name: string;       // Steps
  unit: string;       // step
  dailyGoal: number;  
  fitbitPermission: string;
  conversion?:(n:number)=>string|undefined;
}


interface ITrackersWeekSummary {
  isThisWeek:boolean;
  startDay:moment.Moment;
  lastDay:moment.Moment;
  trackerWeeks:Array<ITrackerWeek>;
}

interface ITrackerWeek {
  tracker:ITracker;
  dayVals:Array<string>;
}

const trackerMoment = (day?:Date):moment.Moment => {
  const _day = day ? day : new Date();
  return moment(_day).startOf('day');
}

/**
 * Takes a daily value and a tracker options and generates a
 * string based on any conversion method
 * 
 * @param tracker 
 * @param dailyValue 
 */
const displayedDailyValue = (tracker:ITracker, dailyValue:IDailyValue) => {
  if(dailyValue) {
    return displayedValue(tracker, dailyValue.value);
  } else {
    return '0';
  }
}

const displayedDailyGoal = (tracker:ITracker) => {
  if(tracker.dailyGoal) {
    return displayedValue(tracker, tracker.dailyGoal);
  } else {
    return '0';
  }
}

const displayedValue = (tracker:ITracker, value:number) => {
  if(value) {
    const conversion = conversionForTracker(tracker.id)
    if(conversion) {
      return conversion(value);
    } else {
      return value;
    }
  } else {
    return '0';
  }
}




class _TrackersData {
  @observable loaded:boolean = false;
  @observable private _trackers:Array<ITracker> = [];

  @computed 
  get defaultTrackerOptions() {
    return UIState.settings.DEF_TRACKER_OPTIONS;
  }

  @computed 
  get trackers(){
    return this._trackers.sort((t1,t2)=>t1.sortOrder-t2.sortOrder);
  }

  @observable private _selectedTracker:ITracker|null = null;
  @computed
  get selectedTracker() {
    return this._selectedTracker;
  }

  // -- Sub Page display state 

  @observable private _backToPage:string = 'home';  
  @action
  selectTracker = (tracker:ITracker, backToPage:string) => {
    this._selectedTracker = tracker;
    this._backToPage = backToPage;
  }

  @action
  deselectTracker = () => {
    this._selectedTracker = null;
    // UIState.currentPage = this._backToPage;
  }

  @observable private _showManageTrackersSubPage:boolean = false;
  set showManageTrackersSubPage(show:boolean) {
    this._showManageTrackersSubPage = show;
    if(show) {
      this.deselectTracker();
    }
  }

  @computed get showTrackerDetailSubPage() { return null!=this.selectedTracker; }
  @computed get showTopLevelSubPages() { return !this._showManageTrackersSubPage && null==this.selectedTracker; }
  @computed get showManageTrackersSubPage() { return this._showManageTrackersSubPage; }

  // -- initialize

  @action
	load = () => {
    if(UIState.settings.ENABLE_TRACKERS) {
      $.ajax(GET_QUERY('/trackers'))
      .done((results:Array<ITracker>)=>{
        results.forEach(_tracker=>{
          if(!_tracker.dailyValues) {
            _tracker.dailyValues = {};
          }
          if(!_tracker.dailyValuesFitbit) {
            _tracker.dailyValuesFitbit = {};
          }
        });
        this._trackers = results;
        this.loaded = true;

        // $.ajax(GET('/fitbit/testAFitbitQuery'))

      })
      .fail();
    }
  }

  @action 
  handleReorderRequest = (start:number, end:number) => {
    let trackers:Array<ITracker> = this._trackers.slice();
    trackers = reorder(trackers, start, end);
    trackers.forEach((tracker,idx) => { tracker.sortOrder = idx; });
    this._trackers = trackers;

    let reorderPayload:Array<any> = [];  
    for(let tracker of this._trackers) {
      reorderPayload.push({trackerId:tracker._id, sortOrder:tracker.sortOrder});
    }

    // Kickoff the server sort, no need to wait for the response   
    $.ajax(POST_QUERY(`/trackers/updateSortOrder`, { orderings: reorderPayload }));
  }

  @action
  disconnectFitbit = () => new Promise<void>((resolve, reject)=>{
    $.ajax(GET_QUERY(`/fitbit/disconnect`))
    .done(()=>{
      UIState.user.fitbitEnabled = false;
      this._trackers.forEach(tracker=>{
        tracker.src = TRACKER_SRC.MANUAL;
      });
      resolve();
    })
    .fail(reject);
  });    


  @action
  updateDailyValue = (tracker:ITracker, dailyValue:IDailyValue) => new Promise<void>((resolve, reject)=>{
    $.ajax(POST_QUERY(`/trackers/updateDailyValue/${tracker._id}`, { dailyValue: dailyValue } ))
    .done((_dailyValue)=>{
      let dv = JSON.parse(JSON.stringify(tracker.dailyValues));
      dv[moment(dailyValue.day).format(TRACKER_DATE_FORMAT)] = _dailyValue;
      tracker.dailyValues= dv; //[moment(dailyValue.day).format(TRACKER_DATE_FORMAT)] = _dailyValue;
      resolve();
    })
    .fail((error)=>{
      reject(error);
    });
  });


  @action
  deleteDailyValues = (tracker:ITracker) => new Promise<void>((resolve, reject)=>{
    $.ajax(GET_QUERY(`/trackers/deleteDailyValues/${tracker._id}`))
    .done(()=>{
      tracker.dailyValues = {};
      resolve();
    })
    .fail((error)=>{
      reject(error);
    });
  });

  @action
  deleteCustomTracker = (tracker:ITracker) => new Promise<void>((resolve, reject)=>{
    $.ajax(GET_QUERY(`/trackers/deleteCustomTracker/${tracker._id}`))
    .done(()=>{
      const idx = this._trackers.findIndex(t=>t._id===tracker._id);
      if(-1!=idx) {
        this._trackers.splice(idx, 1);
      }
      resolve();
    })
    .fail((error)=>{
      reject(error);
    });
  });

  @action 
  toggleTracking = (tracker:ITracker) => new Promise<void>((resolve, reject)=>{
    $.ajax(GET_QUERY(`/trackers/toggleTracking/${tracker._id}`))
    .done((_tracker:ITracker)=>{
      tracker.activeTracking = _tracker.activeTracking;
      resolve();
    })
    .fail(reject);
  });  

  @action 
  updateTracker = (trackerPayload:ITracker) => new Promise<ITracker>((resolve, reject)=>{
    $.ajax(POST_QUERY(`/trackers/updateTracker/${trackerPayload._id}`, trackerPayload))
    .done((savedTracker:ITracker)=>{
      const idx = this._trackers.findIndex(_tracker=>_tracker._id===savedTracker._id);
      if(idx!=-1) {

        // update existing
        this._trackers[idx].dailyGoal = savedTracker.dailyGoal;
        this._trackers[idx].iconName = savedTracker.iconName;
        this._trackers[idx].name = savedTracker.name;
        this._trackers[idx].unit = savedTracker.unit;

        //this._trackers.splice(idx,1,savedTracker);
      } else {
        // insert new. make sure there are dailyValues
        if(!savedTracker.dailyValues) {
          savedTracker.dailyValues = {};
        }
        if(!savedTracker.dailyValuesFitbit) {
          savedTracker.dailyValuesFitbit = {};
        }
        this._trackers.push(savedTracker);
      }
      resolve(savedTracker);
    })
    .fail(reject);    
  });

  @action
  createCustomTracker = (name:string, iconName:string, dailyGoal:number, unit:string) => new Promise((resolve, reject)=>{    
    $.ajax(POST_QUERY(`/trackers/createCustomTracker`, { name, iconName, dailyGoal, unit }))
    .done((savedTracker:ITracker)=>{
      savedTracker.dailyValues = {};
      savedTracker.dailyValuesFitbit = {};
      this._trackers.push(savedTracker);
      resolve(savedTracker);
    })
    .fail(reject);    
  });
  
  @computed get isZeroState() {
    return this.loaded && this.trackers.length===0;
  }

  @computed get inactiveTrackers() {    
    const inactiveTrackers = this.trackers.filter(m=>!m.activeTracking);
    return inactiveTrackers ? inactiveTrackers : [];
  }

  @computed get activeTrackers() {    
    const activeTrackers = this.trackers.filter(m=>m.activeTracking);
    return activeTrackers ? activeTrackers : [];
  }

  // State and functions to support the current daily date for trackers 
  @observable private _dailyMoment:moment.Moment = moment();
  @action
  addDayToDailyMoment = () => {
    if(!this.dailyMomentIsToday) {
      this._dailyMoment = moment(this._dailyMoment).add(1, 'd');
    }
  }
  @action
  subtractDayFromDailyMoment = () => {
    if(!this.dailyMomentIsFirstPortalMoment){
      this._dailyMoment = moment(this._dailyMoment).subtract(1, 'd');
    }
  }
  @computed
  get dailyMoment():moment.Moment {
    return this._dailyMoment;
  }
  @computed
  get firstPortalMoment():moment.Moment {
    if(UIState.settings.PLATFORM_OPEN_DATE) {
      return moment(UIState.settings.PLATFORM_OPEN_DATE);
    } else {
      return this.dailyMoment;
    }
  }
  @computed
  get dailyMomentIsToday() {
    return this.dailyMoment.isSame(moment(), 'day');
  }
  @computed
  get dailyMomentIsFirstPortalMoment() {
    return this.firstPortalMoment.isSame(this.dailyMoment, 'day')
  }


  @observable private _weeklyMoment:moment.Moment = moment().startOf('week');
  @action
  addWeekToWeeklyMoment = () => {
    if(!this.weeklyMomentIsThisWeek) {
      this._weeklyMoment = moment(this._weeklyMoment).add(1, 'week');
    }
  }
  @action
  subtractWeekFromWeeklyMoment = () => {
    if(!this.weeklyMomentIsFirstPortalMoment){
      this._weeklyMoment = moment(this._weeklyMoment).subtract(1, 'week');
    }
  }
  @computed
  get weeklyMoment():moment.Moment {
    return this._weeklyMoment;
  }

  set weeklyMoment(weeklyMoment:moment.Moment) {
    this._weeklyMoment = weeklyMoment;
  }

  @computed
  get firstWeeklyMoment():moment.Moment {
    if(UIState.settings.PORTAL_START_DATE) {
      return moment(UIState.settings.PORTAL_START_DATE).startOf('week');
    } else {
      return this.weeklyMoment.startOf('week');
    }
  }
  @computed
  get weeklyMomentIsThisWeek() {
    return this.weeklyMoment.isSame(moment(), 'week');
  }
  @computed
  get weeklyMomentIsFirstPortalMoment() {
    return this.firstWeeklyMoment.isSame(this.weeklyMoment, 'week')
  }

  @computed
  get allWeeklyMoments() {    
    const weeklyMoments:Array<moment.Moment> = [];
    if(TrackersData.firstWeeklyMoment) {
      let weeklyMoment =  moment().startOf('week');
      weeklyMoments.push(this.weeklyMoment);
      weeklyMoment = moment(this.weeklyMoment).subtract(1,'week');
      while(weeklyMoment.isAfter(TrackersData.firstWeeklyMoment)) {
        weeklyMoments.push(moment(weeklyMoment));
        weeklyMoment.subtract(1,'week');
      }
      weeklyMoments.push(moment(TrackersData.firstWeeklyMoment));
    }
    return weeklyMoments;
  }

  @computed
  get trackersSummaries() {
    const trackersSummaries:Array<ITrackersWeekSummary> = [];
    const thisWeek = moment().startOf('week');
    this.allWeeklyMoments.forEach(weeklyMoment=>{

      const dayKeys:Array<string> = [];
      let day = moment(weeklyMoment);
      dayKeys[0] = day.format(TRACKER_DATE_FORMAT);
      dayKeys[1] = day.add(1,'day').format(TRACKER_DATE_FORMAT);
      dayKeys[2] = day.add(1,'day').format(TRACKER_DATE_FORMAT);
      dayKeys[3] = day.add(1,'day').format(TRACKER_DATE_FORMAT);
      dayKeys[4] = day.add(1,'day').format(TRACKER_DATE_FORMAT);
      dayKeys[5] = day.add(1,'day').format(TRACKER_DATE_FORMAT);
      dayKeys[6] = day.add(1,'day').format(TRACKER_DATE_FORMAT);
      
      const trackerWeeks:Array<ITrackerWeek> = [];
      this.activeTrackers.forEach(tracker=>{
        const dayVals:Array<string> = [];
        const converter = converterForTrackerId(tracker.id);
        if(tracker.src===TRACKER_SRC.FITBIT) {
          dayKeys.forEach((dayKey, idx)=>{
            let dayValue1 = tracker.dailyValuesFitbit[dayKey];
            dayVals[idx] = ( (dayValue1 && dayValue1.value) ? ''+converter(dayValue1.value): '' );
            if(dayVals[idx]==='') {
              dayValue1 = tracker.dailyValues[dayKey];
              dayVals[idx] = ( (dayValue1 && dayValue1.value) ? ''+converter(dayValue1.value): '' );
            }
          });
        } else {
          dayKeys.forEach((dayKey, idx)=>{
            let dayValue1 = tracker.dailyValues[dayKey];
            dayVals[idx] = ( (dayValue1 && dayValue1.value) ? ''+converter(dayValue1.value): '' );
          });
        }        
        trackerWeeks.push({ tracker, dayVals });
      })

      trackersSummaries.push({
        isThisWeek:weeklyMoment.isSame(thisWeek),
        startDay:weeklyMoment,
        lastDay:moment(weeklyMoment).add(6, 'days').endOf('day'),
        trackerWeeks:trackerWeeks
      })

    })

    return trackersSummaries;
  }

}

function converterForTrackerId(trackerId:string){
  if(trackerId===TrackersData.defaultTrackerOptions.SLEEP.id) {
    return sleepConversion;
  } else if(trackerId===TrackersData.defaultTrackerOptions.WATER.id) {
    return waterConversion;
  } else {
    return noConversion
  }
}

function conversionForTracker(trackerId:string){
  if(trackerId===TrackersData.defaultTrackerOptions.SLEEP.id) {
    return sleepConversion;
  } else if(trackerId===TrackersData.defaultTrackerOptions.WATER.id) {
    return waterConversion;
  } else {
    return null;
  }
}

function noConversion(n, reverse?:boolean) {
  return n;
}

// TODO: Test whether rounding with native JS function introduces any problems that mathjs.round avoids.
function sleepConversion(n, reverse?:boolean) { 
  if(n) {  
    if(reverse) {
      // to minutes
      return Number(Math.round(n*60));
    } else {
      // to hours
      return Number((Math.round( (n/60)*100 )/100).toFixed(2));
      // return Number(Math.round(n/60, 2));
    }  
  } else {
    return 0;
  }
}

// TODO: Test whether rounding with native JS function introduces any problems that mathjs.round avoids.
function waterConversion(n, reverse?:boolean):number {
  if(n) {
    if(reverse){
      // to milliliters      
      return Number(Math.round(n*29.5735));
    } else {
      // to ounces
      return Number(Math.round(n*0.033814));
    }
  } else {
    return 0;
  }
}






const TrackersData = new _TrackersData();
export { TrackersData, ITracker, IDailyValue, trackerMoment, TRACKER_DATE_FORMAT, displayedDailyValue, displayedDailyGoal, displayedValue, conversionForTracker, ITrackersWeekSummary, ITrackerWeek }
